import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

function NewsDetail() {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/news/${id}`);
        
        if (!response.ok) {
          setNews(null);
          return;
        }
        
        const data = await response.json();
        setNews(data);
        
        // Buscar notícias relacionadas
        if (data.category) {
          const relResponse = await fetch(`${API}/news?category=${data.category}`);
          if (relResponse.ok) {
            const relData = await relResponse.json();
            const filtered = Array.isArray(relData) ? relData.filter(n => n.id !== id).slice(0, 3) : [];
            setRelatedNews(filtered);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar notícia:', error);
        setNews(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-20">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Notícia não encontrada</h1>
          <p className="text-gray-600 mb-8">A notícia que você procura não existe ou foi removida.</p>
          <Link to="/" className="text-blue-600 hover:underline">← Voltar para home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <Link to="/" className="text-4xl font-bold hover:text-blue-100 transition">📰 NotíciasWeb</Link>
          <p className="text-blue-100 mt-2">Seu portal de notícias atualizado</p>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3 text-sm">
          <Link to="/" className="text-blue-600 hover:underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to={`/?category=${news.category}`} className="text-blue-600 hover:underline">{news.category}</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-600">{news.title}</span>
        </div>
      </div>

      {/* Article */}
      <main className="container mx-auto px-4 py-8">
        <article className="bg-white rounded-lg shadow-lg overflow-hidden max-w-4xl mx-auto">
          {/* Featured Image */}
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-96 object-cover"
          />

          <div className="p-8">
            {/* Category Badge */}
            <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full mb-4">
              {news.category}
            </span>

            {/* Title */}
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              {news.title}
            </h1>

            {/* Meta Info */}
            <div className="flex items-center gap-6 text-gray-600 mb-6 pb-6 border-b">
              <div className="flex items-center gap-2">
                <span>👤</span>
                <span>{news.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>📅</span>
                <span>{new Date(news.date).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}</span>
              </div>
            </div>

            {/* Summary */}
            <p className="text-xl text-gray-700 font-medium mb-8 leading-relaxed">
              {news.summary}
            </p>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {news.content.split('\n\n').map((paragraph, index) => {
                // Verificar se é um heading
                if (paragraph.startsWith('## ')) {
                  return (
                    <h2 key={index} className="text-2xl font-bold text-gray-800 mt-8 mb-4">
                      {paragraph.replace('## ', '')}
                    </h2>
                  );
                }
                
                if (paragraph.startsWith('### ')) {
                  return (
                    <h3 key={index} className="text-xl font-bold text-gray-800 mt-6 mb-3">
                      {paragraph.replace('### ', '')}
                    </h3>
                  );
                }
                
                // Verificar se é uma lista
                if (paragraph.includes('- ')) {
                  const items = paragraph.split('\n').filter(line => line.trim().startsWith('- '));
                  return (
                    <ul key={index} className="list-disc pl-6 text-gray-800 mb-6">
                      {items.map((item, i) => (
                        <li key={i} className="mb-2">{item.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>
                      ))}
                    </ul>
                  );
                }
                
                // Parágrafo normal
                return (
                  <p key={index} className="text-gray-800 mb-6 leading-relaxed">
                    {paragraph.split('**').map((part, i) => 
                      i % 2 === 0 ? part : <strong key={i}>{part}</strong>
                    )}
                  </p>
                );
              })}
            </div>

            {/* ✅ ANÚNCIO APENAS DEPOIS DO CONTEÚDO - CRÍTICO PARA ADSENSE */}
            <div className="my-12 p-8 bg-gray-50 rounded-lg text-center border-2 border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Publicidade</p>
              {/* Google AdSense - SUBSTITUIR ESTE COMENTÁRIO PELO CÓDIGO REAL APÓS APROVAÇÃO */}
              <div className="min-h-[250px] flex items-center justify-center bg-white rounded">
                <p className="text-gray-400 text-sm">
                  Espaço reservado para Google AdSense<br/>
                  (Inserir código após aprovação)
                </p>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="mt-8 pt-6 border-t">
              <p className="text-gray-700 font-medium mb-4">Compartilhe esta notícia:</p>
              <div className="flex gap-4">
                <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">
                  📘 Facebook
                </button>
                <button className="px-4 py-2 bg-sky-500 text-white rounded hover:bg-sky-600 transition">
                  🐦 Twitter
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition">
                  📱 WhatsApp
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related News */}
        {relatedNews.length > 0 && (
          <div className="max-w-4xl mx-auto mt-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Notícias Relacionadas</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/noticia/${item.id}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition"
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-bold text-gray-800 mb-2 line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{item.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-2">📰 NotíciasWeb</h3>
            <p className="text-gray-400">Seu portal de notícias atualizado</p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/sobre" className="text-gray-400 hover:text-white">Sobre</Link>
              <Link to="/privacidade" className="text-gray-400 hover:text-white">Privacidade</Link>
              <Link to="/termos" className="text-gray-400 hover:text-white">Termos</Link>
              <Link to="/contato" className="text-gray-400 hover:text-white">Contato</Link>
            </div>
            <p className="text-gray-500 text-sm mt-4">
              © {new Date().getFullYear()} NotíciasWeb. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default NewsDetail;